import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import Typography from 'Components/UI/Typography';
import Button from 'Components/UI/Button';
import useEventTrackingLogEvent from 'EventTracking/useEventTrackingLogEvent';
import { fullGeneralDate } from 'Consts/DateFormats';
import NavigateNextIcon from '@material-ui/icons/NavigateNext';
import NavigateBeforeIcon from '@material-ui/icons/NavigateBefore';
import IconButton from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/IconButton';
import BackIcon from '@material-ui/icons/ArrowBackIos';
import classnames from 'classnames';

const styles = theme => ({
  root: {
    backgroundColor: theme.palette.facet.primaryBlue,
  },
  content: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%',
    maxWidth: theme.dimensions.xxl,
    margin: '0 auto',
    padding: theme.spacing(0, 2),
    color: theme.palette.common.white,
  },
  monthLabel: {
    width: '200px',
    textAlign: 'center',
  },
});

const DateNavigator = ({
  classes,
  className,
  contentClassName,
  date,
  prevDate,
  nextDate,
  goToNextDate,
  goToPrevDate,
  setDateToToday,
  goToNextDateEventName,
  goToPrevDateEventName,
  goToNextDateEventProperties,
  goToPrevDateEventProperties,
  isLoading,
  goToMonthlyView,
  isWeekly,
  BackIconButtonProps,
  PrevIconButtonProps,
  NextIconButtonProps,
  MonthLabelProps,
}) => {
  const { rootNodeProps, logEvent } = useEventTrackingLogEvent({
    nodeName: 'Date Navigator',
  });

  return (
    <div
      className={classnames(classes.root, className)}
      {...rootNodeProps}
    >
      <div className={classnames(classes.content, contentClassName)}>
        {isWeekly && isWeekly() && (
          <IconButton
            color="white"
            title={'Back to Monthly View'}
            icon={BackIcon}
            onClick={goToMonthlyView}
            label={'Back'}
            size={'small'}
            {...BackIconButtonProps}
          >
            {'Back'}
          </IconButton>
        )}
        {setDateToToday && (
          <Button variant="outlined" onClick={setDateToToday}>Today</Button>
        )}
        <IconButton
          color="white"
          title={`View ${prevDate.format(fullGeneralDate)}`}
          icon={NavigateBeforeIcon}
          test="date-navigator-prev-date-button"
          onClick={() => {
            logEvent(goToPrevDateEventName, goToPrevDateEventProperties);
            goToPrevDate();
          }}
          disabled={isLoading}
          {...PrevIconButtonProps}
        />
        <Typography
          color="inherit"
          variant="h4"
          className={classes.monthLabel}
          test="date-navigator-date"
          data-test-navigator-date={date.toISOString()}
          {...MonthLabelProps}
        >
          {moment(date).format(fullGeneralDate)}
        </Typography>
        <IconButton
          color="white"
          title={`View ${nextDate.format(fullGeneralDate)}`}
          icon={NavigateNextIcon}
          test="date-navigator-next-date-button"
          onClick={() => {
            logEvent(goToNextDateEventName, goToNextDateEventProperties);
            goToNextDate();
          }}
          disabled={isLoading}
          {...NextIconButtonProps}
        />
      </div>
    </div>
  );
};

DateNavigator.defaultProps = {
  className: '',
  contentClassName: '',
  setDateToToday: null,
  isLoading: false,
  goToNextDateEventProperties: {},
  goToPrevDateEventProperties: {},
};

DateNavigator.propTypes = {
  className: PropTypes.string,
  contentClassName: PropTypes.string,
  isLoading: PropTypes.bool,
  classes: PropTypes.object.isRequired,
  date: PropTypes.object.isRequired,
  prevDate: PropTypes.object.isRequired,
  nextDate: PropTypes.object.isRequired,
  setDateToToday: PropTypes.func,
  goToNextDate: PropTypes.func.isRequired,
  goToPrevDate: PropTypes.func.isRequired,
  goToNextDateEventName: PropTypes.string.isRequired,
  goToPrevDateEventName: PropTypes.string.isRequired,
  goToNextDateEventProperties: PropTypes.object,
  goToPrevDateEventProperties: PropTypes.object,
};

export default withStyles(styles)(DateNavigator);
